'use client';

import { useEffect, useRef, useState } from 'react';
import { useBrowserState } from './use-browser-state';

export type VoiceCommandState = 'idle' | 'listening' | 'unsupported';

export interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult:
    | ((event: {
        results: ArrayLike<ArrayLike<{ transcript: string }>>;
        resultIndex: number;
      }) => void)
    | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

interface SpeechRecognitionWindow {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function isVoiceSupported() {
  if (typeof window === 'undefined') return false;
  const browser = window as unknown as SpeechRecognitionWindow;
  return Boolean(browser.SpeechRecognition ?? browser.webkitSpeechRecognition);
}

export function useVoiceCommands(
  onCommand: (transcript: string) => void,
): {
  state: VoiceCommandState;
  transcript: string;
  start: () => void;
  stop: () => void;
} {
  const [state, setState] = useState<VoiceCommandState>('idle');
  const [supported, , ready] = useBrowserState(isVoiceSupported, false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as unknown as SpeechRecognitionWindow;
    const SpeechRecognition = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        finalTranscript += event.results[i]?.[0]?.transcript ?? '';
      }
      if (finalTranscript) setTranscript(finalTranscript);
    };
    recognition.onend = () => setState((s) => (s === 'listening' ? 'idle' : s));
    recognitionRef.current = recognition;
    return () => {
      recognition.onend = null;
      recognition.onresult = null;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [supported]);

  function start() {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.start();
      setState('listening');
      setTranscript('');
    } catch {}
  }

  function stop() {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch {}
    setState('idle');
    if (transcript) onCommand(transcript);
  }

  return { state: ready && !supported ? 'unsupported' : state, transcript, start, stop };
}

export const VOICE_COMMAND_MAP = [
  { patterns: [/dashboard|übersicht/i], action: 'navigation:dashboard' },
  {
    patterns: [/rechnung.*erstellen|neue.*rechnung/i],
    action: 'workflow:first-invoice',
  },
  { patterns: [/bankimport/i], action: 'workflow:bank-import' },
  { patterns: [/monatsabschluss/i], action: 'workflow:monthly-close' },
  { patterns: [/hilfe/i], action: 'help:open' },
] as const satisfies ReadonlyArray<{
  readonly patterns: readonly RegExp[];
  readonly action: string;
}>;

export function matchVoiceCommand(transcript: string): string | null {
  for (const { patterns, action } of VOICE_COMMAND_MAP) {
    for (const pattern of patterns) {
      if (pattern.test(transcript)) return action;
    }
  }
  return null;
}
